"use client";

import { useCallback, useMemo } from "react";
import {
  SourceCitationBuilder,
  type SourceField,
  type SourceValues,
} from "@/components/tool-kit/source-citation-builder";
import { useToolUi } from "@/components/tools/tool-ui";
import {
  POSTER_FIELDS as F,
  formatPoster,
  type PosterLabels,
  posterIsReady,
} from "@/lib/tool-kit/citation-sources-more/poster-citation-generator";

/** English strings. A translated content file overrides these through `ui`. */
export const POSTER_CITATION_GENERATOR_STRINGS = {
  formHeading: "Poster details",
  resultsHeading: "Your citations",
  emptyPrompt: "Enter the poster's title to see your citations.",
  copy: "Copy",
  copied: "Copied!",
  tryExample: "Try an example",
  clear: "Clear",
  authorsLabel: "Presenter(s)",
  authorsPlaceholder: "R. Fothergill",
  authorsHint:
    "One per line, as First Last or Last, First, in the order printed on the poster. Initials are fine if that is all the poster gives.",
  titleLabel: "Poster title",
  titlePlaceholder:
    "Intentions to work with older adults: A critical exploration",
  titleHint:
    "Type it in sentence case: capitalize only the first word, the first word after a colon and proper nouns. MLA and Chicago get title case automatically.",
  conferenceLabel: "Conference name",
  conferencePlaceholder: "British Psychological Society Annual Conference",
  conferenceHint:
    "The full name of the meeting, as the program prints it, with its edition number if it has one.",
  locationLabel: "Location",
  locationPlaceholder: "Nottingham",
  locationHint:
    "City, and the state or province and country where they help. Type Online for a virtual conference.",
  startLabel: "First day of the conference",
  endLabel: "Last day of the conference (optional)",
  datePlaceholder: "YYYY-MM-DD",
  endHint:
    "APA dates a poster by the whole run of the conference, not by the hour of your session.",
  urlLabel: "URL of the poster (optional)",
  urlPlaceholder: "http://eprints.lincoln.ac.uk/id/eprint/24576/",
  urlHint:
    "Link to the poster itself or its DOI. If there is none, the conference program page is the fallback.",
  shortTitleLabel: "Short title for notes (Chicago, optional)",
  shortTitlePlaceholder: "Intentions to Work",
  shortTitleHint:
    "Chicago shortens a long title to its key words, by hand. Leave blank for a suggestion.",
  apaLabel: "APA 7th edition",
  mlaLabel: "MLA 9th edition",
  chicagoLabel: "Chicago 18th edition (notes and bibliography)",
  referenceList: "Reference list",
  parenthetical: "In-text citation (parenthetical)",
  narrative: "In-text citation (narrative)",
  worksCited: "Works Cited",
  inText: "In-text citation",
  footnote: "Footnote",
  shortNote: "Shortened note",
  bibliography: "Bibliography",
  apaNote:
    "APA 7 puts the poster's title in italics with [Poster presentation] after it, then the conference and its location. The date is the whole run of the conference.",
  mlaNote:
    "Follows the MLA Style Center's answer on a paper presented at a conference: title in quotation marks, conference name and date, then location. The closing label Poster presentation is a library-guide convention, not an MLA example. Drop it if you prefer.",
  chicagoNote:
    "Chicago 18 (§14.115) writes poster presented at, then the meeting, place and date, with no parentheses. Chicago normally keeps a poster in the note or the text; the bibliography entry is for when your instructor asks for one.",
  example: {
    authors: "R. Fothergill\nR. Bromnick",
    title:
      "Intentions to work with older adults: A critical exploration of psychology students’ motivations and ambition",
    conference: "British Psychological Society Annual Conference",
    location: "Nottingham",
    start: "2016-04-26",
    end: "2016-04-28",
    url: "http://eprints.lincoln.ac.uk/id/eprint/24576/",
    shortTitle: "Intentions to Work with Older Adults",
  },
} as const;

type Strings = typeof POSTER_CITATION_GENERATOR_STRINGS;

function buildFields(strings: Strings): SourceField[] {
  return [
    {
      id: F.authors,
      kind: "people",
      label: strings.authorsLabel,
      placeholder: strings.authorsPlaceholder,
      hint: strings.authorsHint,
    },
    {
      id: F.title,
      kind: "text",
      label: strings.titleLabel,
      placeholder: strings.titlePlaceholder,
      hint: strings.titleHint,
      wide: true,
    },
    {
      id: F.conference,
      kind: "text",
      label: strings.conferenceLabel,
      placeholder: strings.conferencePlaceholder,
      hint: strings.conferenceHint,
    },
    {
      id: F.location,
      kind: "text",
      label: strings.locationLabel,
      placeholder: strings.locationPlaceholder,
      hint: strings.locationHint,
    },
    {
      id: F.start,
      kind: "date",
      label: strings.startLabel,
      placeholder: strings.datePlaceholder,
    },
    {
      id: F.end,
      kind: "date",
      label: strings.endLabel,
      placeholder: strings.datePlaceholder,
      hint: strings.endHint,
    },
    {
      id: F.url,
      kind: "url",
      label: strings.urlLabel,
      placeholder: strings.urlPlaceholder,
      hint: strings.urlHint,
    },
    {
      id: F.shortTitle,
      kind: "text",
      label: strings.shortTitleLabel,
      placeholder: strings.shortTitlePlaceholder,
      hint: strings.shortTitleHint,
    },
  ];
}

/** The widget's strings, as the formatter's labels. */
export function posterLabels(strings: Strings): PosterLabels {
  return {
    apa: strings.apaLabel,
    mla: strings.mlaLabel,
    chicago: strings.chicagoLabel,
    referenceList: strings.referenceList,
    parenthetical: strings.parenthetical,
    narrative: strings.narrative,
    worksCited: strings.worksCited,
    inText: strings.inText,
    footnote: strings.footnote,
    shortNote: strings.shortNote,
    bibliography: strings.bibliography,
    apaNote: strings.apaNote,
    mlaNote: strings.mlaNote,
    chicagoNote: strings.chicagoNote,
  };
}

export default function PosterCitationGeneratorWidget() {
  const strings = useToolUi(POSTER_CITATION_GENERATOR_STRINGS);
  const fields = useMemo(() => buildFields(strings), [strings]);
  const labels = useMemo(() => posterLabels(strings), [strings]);
  const format = useCallback(
    (values: SourceValues) => formatPoster(values, labels),
    [labels]
  );

  return (
    <SourceCitationBuilder
      example={strings.example}
      fields={fields}
      format={format}
      isReady={posterIsReady}
      strings={strings}
    />
  );
}
